import React from "react";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  errorMessage?: string;
};

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Loading chunk")
  );
}

/**
 * Recovers from stale asset caches (common after a new deploy) by doing a single hard reload
 * when a route chunk fails to load.
 */
export class ChunkLoadErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, errorMessage: message };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) return;

    // Avoid infinite reload loops
    const key = "__chunk_reload_once__";
    const alreadyReloaded = sessionStorage.getItem(key) === "1";
    if (alreadyReloaded) return;

    sessionStorage.setItem(key, "1");
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-3xl bg-card p-6 shadow-soft text-center space-y-3">
          <h1 className="font-serif text-xl text-foreground">רגע…</h1>
          <p className="text-sm text-muted-foreground">
            נראה שהייתה בעיית טעינה זמנית. נסו לרענן את העמוד.
          </p>
          <button
            className="inline-flex items-center justify-center rounded-2xl bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            רענון
          </button>
        </div>
      </div>
    );
  }
}
